import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../components/layout/MainLayout';
import { containerApi, Container } from '../../services/api/container.api';

export default function ContainersPage() {
  const { t } = useTranslation(['infrastructure', 'common']);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [containers, setContainers] = useState<Container[]>([]);
  const [selectedContainer, setSelectedContainer] = useState<Container | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalContainers, setTotalContainers] = useState(0);

  // Filters
  const [filterHost, setFilterHost] = useState<string>('all');
  const [filterProject, setFilterProject] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  // Extract unique hosts and projects from containers
  const { uniqueHosts, uniqueProjects } = useMemo(() => {
    const hosts = new Set<string>();
    const projects = new Set<string>();

    containers.forEach((c) => {
      if (c.hostname || c.tags?.host_name) {
        hosts.add(c.hostname || c.tags?.host_name || '');
      }
      if (c.tags?.compose_project) {
        projects.add(c.tags.compose_project);
      }
    });

    return {
      uniqueHosts: Array.from(hosts).filter(Boolean).sort(),
      uniqueProjects: Array.from(projects).filter(Boolean).sort(),
    };
  }, [containers]);

  // Filtered containers
  const filteredContainers = useMemo(() => {
    return containers.filter((c) => {
      const hostMatch = filterHost === 'all' || c.hostname === filterHost || c.tags?.host_name === filterHost;
      const projectMatch = filterProject === 'all' || c.tags?.compose_project === filterProject;
      const statusMatch = filterStatus === 'all' || c.status === filterStatus;
      return hostMatch && projectMatch && statusMatch;
    });
  }, [containers, filterHost, filterProject, filterStatus]);

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const offset = (currentPage - 1) * pageSize;
      const result = await containerApi.getContainers(pageSize, offset);
      setContainers(result.data);
      setTotalContainers(result.meta.total);
    } catch (err: any) {
      setError(err.message || 'Failed to load containers');
      console.error('Containers fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'offline':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      case 'degraded':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'unknown':
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600 dark:text-gray-400">{t('infrastructure:containers.loading')}</div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-4">
          <p className="text-red-800 dark:text-red-400">Error: {error}</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          {t('infrastructure:containers.title')}
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          {t('infrastructure:containers.subtitle')}
        </p>
      </div>

      {/* Stats Cards */}
      <div className="mb-6 grid grid-cols-1 gap-6 sm:grid-cols-5">
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <p className="text-sm text-gray-600 dark:text-gray-400">{t('infrastructure:containers.totalContainers')}</p>
          <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{filteredContainers.length}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <p className="text-sm text-gray-600 dark:text-gray-400">{t('infrastructure:containers.online')}</p>
          <p className="mt-2 text-3xl font-bold text-green-600 dark:text-green-400">
            {filteredContainers.filter((c) => c.status === 'online').length}
          </p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <p className="text-sm text-gray-600 dark:text-gray-400">{t('infrastructure:containers.degraded')}</p>
          <p className="mt-2 text-3xl font-bold text-yellow-600 dark:text-yellow-400">
            {filteredContainers.filter((c) => c.status === 'degraded').length}
          </p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <p className="text-sm text-gray-600 dark:text-gray-400">{t('infrastructure:containers.offline')}</p>
          <p className="mt-2 text-3xl font-bold text-red-600 dark:text-red-400">
            {filteredContainers.filter((c) => c.status === 'offline').length}
          </p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <p className="text-sm text-gray-600 dark:text-gray-400">{t('infrastructure:containers.hosts')}</p>
          <p className="mt-2 text-3xl font-bold text-indigo-600 dark:text-indigo-400">{uniqueHosts.length}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900">
        <div className="flex flex-wrap gap-4">
          {/* Host Filter */}
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('infrastructure:containers.filters.host')}
            </label>
            <select
              value={filterHost}
              onChange={(e) => setFilterHost(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
            >
              <option value="all">{t('infrastructure:containers.filters.allHosts')} ({uniqueHosts.length})</option>
              {uniqueHosts.map((host) => (
                <option key={host} value={host}>
                  {host}
                </option>
              ))}
            </select>
          </div>

          {/* Project Filter */}
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('infrastructure:containers.filters.project')}
            </label>
            <select
              value={filterProject}
              onChange={(e) => setFilterProject(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
            >
              <option value="all">{t('infrastructure:containers.filters.allProjects')} ({uniqueProjects.length})</option>
              {uniqueProjects.map((project) => (
                <option key={project} value={project}>
                  {project}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div className="flex-1 min-w-[150px]">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('infrastructure:containers.filters.status')}
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
            >
              <option value="all">{t('infrastructure:containers.filters.allStatuses')}</option>
              <option value="online">{t('infrastructure:containers.online')}</option>
              <option value="degraded">{t('infrastructure:containers.degraded')}</option>
              <option value="offline">{t('infrastructure:containers.offline')}</option>
            </select>
          </div>

          {/* Clear Filters */}
          {(filterHost !== 'all' || filterProject !== 'all' || filterStatus !== 'all') && (
            <div className="flex items-end">
              <button
                onClick={() => {
                  setFilterHost('all');
                  setFilterProject('all');
                  setFilterStatus('all');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
              >
                {t('infrastructure:containers.filters.clearFilters')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Containers List */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
        <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-800">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('infrastructure:containers.title')}</h3>
        </div>
        {filteredContainers.length === 0 ? (
          <div className="p-6 text-center text-gray-500 dark:text-gray-400">
            {containers.length === 0 ? t('infrastructure:containers.noContainersFound') : t('infrastructure:containers.noContainersFiltered')}
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-800">
            {filteredContainers.map((container) => (
              <div
                key={container.id}
                className="cursor-pointer p-6 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50"
                onClick={() => setSelectedContainer(selectedContainer?.id === container.id ? null : container)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center flex-wrap gap-2 mb-2">
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white">{container.name}</h4>
                      <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${getStatusColor(container.status)}`}>
                        {container.status}
                      </span>
                      {/* Host Badge */}
                      {(container.hostname || container.tags?.host_name) && (
                        <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 dark:bg-blue-900/20 dark:text-blue-400">
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                          </svg>
                          {container.hostname || container.tags?.host_name}
                        </span>
                      )}
                      {/* Compose Project Badge */}
                      {container.tags?.compose_project && (
                        <span className="inline-flex items-center rounded-md bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700 dark:bg-purple-900/20 dark:text-purple-400">
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                          </svg>
                          {container.tags.compose_project}
                          {container.tags?.compose_service && (
                            <span className="ml-1 opacity-75">/ {container.tags.compose_service}</span>
                          )}
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4 mt-4 md:grid-cols-4">
                      {container.tags?.image && (
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{t('infrastructure:containers.image')}</p>
                          <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white truncate" title={container.tags.image}>
                            {container.tags.image}
                          </p>
                        </div>
                      )}
                      {container.tags?.state && (
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{t('infrastructure:containers.state')}</p>
                          <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                            {container.tags.state}
                          </p>
                        </div>
                      )}
                      {container.ip_address && (
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{t('infrastructure:containers.ipAddress')}</p>
                          <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                            {container.ip_address}
                          </p>
                        </div>
                      )}
                      {container.tags?.restart_count !== undefined && (
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{t('infrastructure:containers.restarts')}</p>
                          <p className={`mt-1 text-sm font-medium ${
                            container.tags.restart_count > 5 ? 'text-red-600 dark:text-red-400' :
                            container.tags.restart_count > 0 ? 'text-yellow-600 dark:text-yellow-400' :
                            'text-green-600 dark:text-green-400'
                          }`}>
                            {container.tags.restart_count}
                          </p>
                        </div>
                      )}
                    </div>

                    {container.tags?.ports && container.tags.ports.length > 0 && (
                      <div className="mt-4">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{t('infrastructure:containers.ports')}</p>
                        <div className="flex flex-wrap gap-2">
                          {container.tags.ports.map((port, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-400"
                            >
                              {port}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {selectedContainer?.id === container.id && (
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </div>

                {selectedContainer?.id === container.id && (
                  <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                    <h5 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
                      {t('infrastructure:containers.containerDetails')}
                    </h5>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">{t('infrastructure:containers.containerId')}:</span>
                        <p className="mt-1 font-mono text-xs text-gray-900 dark:text-white break-all">
                          {container.id}
                        </p>
                      </div>
                      {container.tags?.container_id && (
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">{t('infrastructure:containers.runtimeId')}:</span>
                          <p className="mt-1 font-mono text-xs text-gray-900 dark:text-white break-all">
                            {container.tags.container_id}
                          </p>
                        </div>
                      )}
                      {container.cloud_provider && (
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">{t('infrastructure:containers.cloudProvider')}:</span>
                          <p className="mt-1 text-gray-900 dark:text-white">
                            {container.cloud_provider}
                          </p>
                        </div>
                      )}
                      {container.cloud_region && (
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">{t('infrastructure:containers.region')}:</span>
                          <p className="mt-1 text-gray-900 dark:text-white">
                            {container.cloud_region}
                          </p>
                        </div>
                      )}
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">{t('infrastructure:containers.created')}:</span>
                        <p className="mt-1 text-gray-900 dark:text-white">
                          {new Date(container.created_at).toLocaleString()}
                        </p>
                      </div>
                      {container.last_heartbeat_at && (
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">{t('infrastructure:containers.lastHeartbeat')}:</span>
                          <p className="mt-1 text-gray-900 dark:text-white">
                            {new Date(container.last_heartbeat_at).toLocaleString()}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {containers.length > 0 && (
          <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-800 dark:bg-gray-800/50">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {t('infrastructure:pagination.showing')} {((currentPage - 1) * pageSize) + 1} {t('infrastructure:pagination.to')} {Math.min(currentPage * pageSize, totalContainers)} {t('infrastructure:pagination.of')} {totalContainers} {t('infrastructure:pagination.totalContainers')}
              </span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="ml-4 rounded border border-gray-300 bg-white px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300"
              >
                <option value={10}>10 {t('infrastructure:pagination.perPage')}</option>
                <option value={20}>20 {t('infrastructure:pagination.perPage')}</option>
                <option value={50}>50 {t('infrastructure:pagination.perPage')}</option>
              </select>
            </div>

            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="rounded border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                {t('infrastructure:pagination.first')}
              </button>
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="rounded border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                {t('infrastructure:pagination.previous')}
              </button>
              <span className="flex items-center px-3 text-sm text-gray-700 dark:text-gray-300">
                {t('infrastructure:pagination.page')} {currentPage} {t('infrastructure:pagination.of')} {Math.ceil(totalContainers / pageSize)}
              </span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage >= Math.ceil(totalContainers / pageSize)}
                className="rounded border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                {t('infrastructure:pagination.next')}
              </button>
              <button
                onClick={() => setCurrentPage(Math.ceil(totalContainers / pageSize))}
                disabled={currentPage >= Math.ceil(totalContainers / pageSize)}
                className="rounded border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                {t('infrastructure:pagination.last')}
              </button>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
